import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdEmotionalWaveGlyphComponent } from './hd-emotional-wave-glyph.component';
import { HdEmotionalWavesPanelService } from './hd-emotional-waves-panel.service';
import {
  HdEmotionalWaveKey,
  HdEmotionalWaveRef,
  hdEmotionalWaveForSourceLine,
} from './hd-emotional-waves.metadata';

interface WaveRowVm {
  readonly label: string;
  readonly name: string;
  readonly keynote: string;
  readonly openLabel: string;
  /** Bron: de golven van de geactiveerde lijnen van poort 6. */
  readonly highlight: readonly HdEmotionalWaveKey[];
  /** Bron: "Lijn 2: tribale golf · …", leeg zonder lijnen. */
  readonly lineNote: string;
}

/**
 * Golfregel van een emotionele poort of een emotioneel kanaal: "Emotionele
 * golf:" + golfje + golfnaam (klikbaar → golvenpaneel op die golf) en daaronder
 * de keynote. Bij de bron (poort 6 / kanaal 59-6) worden de golven van de
 * geactiveerde lijnen van poort 6 uitgelicht. Gedeeld door het poort-
 * infoscherm en de kanaal-info; de lettergrootte en inspringing komen van de
 * ouder.
 */
@Component({
  selector: 'hd-emotional-wave-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdEmotionalWaveGlyphComponent],
  template: `
    <div class="hd-emotional-wave-row__main">
      <span class="hd-emotional-wave-row__label">{{ vm().label }}:</span>
      <button
        type="button"
        class="hd-emotional-wave-row__button"
        data-test-id="emotional-wave-open"
        [title]="vm().openLabel"
        [attr.aria-label]="vm().name + ': ' + vm().openLabel"
        (click)="open($event)"
      >
        <hd-emotional-wave-glyph [wave]="wave()" [highlight]="vm().highlight" />
        <span class="hd-emotional-wave-row__name">{{ vm().name }}</span>
      </button>
    </div>
    @if (vm().keynote) {
      <p class="hd-emotional-wave-row__keynote">{{ vm().keynote }}</p>
    }
    @if (vm().lineNote) {
      <p class="hd-emotional-wave-row__lines" data-test-id="emotional-wave-lines">{{ vm().lineNote }}</p>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .hd-emotional-wave-row__main {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
    }

    .hd-emotional-wave-row__label {
      opacity: 0.6;
    }

    .hd-emotional-wave-row__button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      padding: 1px 4px;
      border: none;
      border-radius: 4px;
      background: none;
      color: inherit;
      font: inherit;
      cursor: pointer;
    }

    .hd-emotional-wave-row__button:hover,
    .hd-emotional-wave-row__button:focus-visible {
      background: rgba(61, 65, 78, 0.08);
    }

    .hd-emotional-wave-row__button:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 1px;
    }

    .hd-emotional-wave-row__name {
      font-weight: 600;
      text-decoration: underline dotted rgba(61, 65, 78, 0.45);
      text-underline-offset: 3px;
    }

    .hd-emotional-wave-row__keynote,
    .hd-emotional-wave-row__lines {
      margin: 1px 0 0;
      line-height: 1.4;
    }

    .hd-emotional-wave-row__lines {
      opacity: 0.7;
      font-size: 0.92em;
    }
  `,
})
export class HdEmotionalWaveRowComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly panel = inject(HdEmotionalWavesPanelService);

  readonly wave = input.required<HdEmotionalWaveRef>();
  /** Scope-sleutel van de keynote, bv. `gates.36.keynote` of `channels.35-36.keynote`. */
  readonly keynoteKey = input('');
  /** Enkel bij de bron: de geactiveerde lijnen van poort 6. */
  readonly sourceLines = input<readonly number[]>([]);
  /** Gedefinieerde kanalen van de chart, doorgegeven aan het golvenpaneel; afwezig = geen badges. */
  readonly definedChannelKeys = input<readonly string[] | undefined>(undefined);

  protected readonly vm = computed<WaveRowVm>(() => {
    const wave = this.wave();
    const lang = this.contentLang.effectiveLang();
    const text = (key: string) =>
      this.gateInfo.cheatSheetTextIn('emotionalWaves', key, lang)
      || this.gateInfo.cheatSheetTextIn('emotionalWaves', key, 'nl');
    const lineWaves = wave === 'source'
      ? [...new Set(this.sourceLines())]
        .sort((a, b) => a - b)
        .map(line => ({ line, wave: hdEmotionalWaveForSourceLine(line) }))
        .filter((entry): entry is { line: number; wave: HdEmotionalWaveKey } => entry.wave != null)
      : [];
    const lineLabel = text('gateRow.lineLabel');
    const keynoteKey = this.keynoteKey();
    return {
      label: text('gateRow.label'),
      name: wave === 'source' ? text('source.shortName') : text(`waves.${wave}.name`),
      keynote: keynoteKey ? text(keynoteKey) : '',
      openLabel: text('gateRow.openPanel'),
      highlight: [...new Set(lineWaves.map(entry => entry.wave))],
      lineNote: lineWaves
        .map(entry => `${lineLabel} ${entry.line}: ${text(`waves.${entry.wave}.name`).toLocaleLowerCase(lang)}`)
        .join(' · '),
    };
  });

  protected open(event: MouseEvent): void {
    this.panel.openForWave(
      this.wave(),
      { x: event.clientX, y: event.clientY },
      this.definedChannelKeys(),
    );
  }
}
